"use client";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Flex, Grid } from "@radix-ui/themes";
import { useActionState, useEffect, useState } from "react";

import { GetClientsWithStats } from "@/app/actions/clients/actions";
import { ClientWithStats } from "@/types/models";

import { CreateContact } from "@/app/actions/contacts/actions";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { SearchParams } from "@/types/listing";
import { AlertTriangle } from "lucide-react";
import { useRouter } from "next/navigation";
import { useMemo } from "react";

type StateType = {
  error?: string | undefined;
};

export default function Create({
  searchParams,
}: {
  searchParams: SearchParams & { client: string | undefined };
}) {
  const router = useRouter();

  const { client } = searchParams;

  const [clients, setClients] = useState<ClientWithStats[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>(client || "");

  const getClientsWithStats = useMemo(
    () => async () => {
      console.log("Getting clients");
      const { data, error } = await GetClientsWithStats();
      if (error) {
        console.log(error);
        return [];
      }
      return data;
    },
    [],
  );

  useEffect(() => {
    // setIsLoading(true);
    console.log("Getting clients");
    getClientsWithStats().then((clients: ClientWithStats[]) => {
      setClients(clients);
    });

    /* trunk-ignore(eslint/react-hooks/exhaustive-deps) */
  }, []);

  const [formState, formAction, formIsPending] = useActionState(
    async (_previousState: any, formData: FormData): Promise<StateType> => {
      if (!selectedClient) {
        return {
          error: "Client is required",
        };
      }

      const firstName = formData.get("firstName")?.toString();
      if (!firstName) {
        return {
          error: "First name is required",
        };
      }

      const lastName = formData.get("lastName")?.toString();
      if (!lastName) {
        return {
          error: "Last name is required",
        };
      }

      const email = formData.get("email")?.toString();
      if (!email) {
        return {
          error: "Email is required",
        };
      }

      const { data, error } = await CreateContact({
        clientId: selectedClient,
        firstName,
        lastName,
        email,
        phone: formData.get("phone")?.toString(),
      });

      if (error || !data) {
        return {
          error: error || "Failed to create contact",
        };
      } else {
        router.push(`/contacts/${data[0]?.id}`);
        return {};
      }
    },
    null,
  );

  return (
    <Flex gap="3" direction="column" className="w-full max-w-3xl">
      <form action={formAction}>
        <Card>
          <CardHeader>
            <CardTitle>Create a Contact</CardTitle>
            <CardDescription>Create a contact for a client</CardDescription>
          </CardHeader>
          <CardContent>
            {formState?.error && (
              <Alert variant="destructive" className="mb-4 mt-4">
                <AlertTriangle className="h-6 w-6" />
                <AlertTitle className="!pl-8">Error</AlertTitle>
                <AlertDescription className="!pl-8">
                  {formState.error}
                </AlertDescription>
              </Alert>
            )}
            <Flex direction="column" gap="4" className="w-full">
              <Grid columns="1" gap="4">
                <Flex direction="column" gap="2">
                  <Label htmlFor="primaryContact">Client</Label>
                  <Flex>
                    {clients.length === 0 ? (
                      <Skeleton className="w-full h-10" />
                    ) : (
                      <Combobox
                        entity="Client"
                        values={clients.map((client) => ({
                          label: client.name,
                          value: client.id,
                        }))}
                        className="w-full"
                        value={selectedClient}
                        onChange={(val) => setSelectedClient(val)}
                      />
                    )}
                  </Flex>
                </Flex>
              </Grid>
              <Grid columns="2" gap="4">
                <Flex direction="column" gap="2">
                  <Label htmlFor="firstName">First Name</Label>
                  <input
                    type="text"
                    name="firstName"
                    id="firstName"
                    className="input"
                  />
                </Flex>
                <Flex direction="column" gap="2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <input
                    type="text"
                    name="lastName"
                    id="lastName"
                    className="input"
                  />
                </Flex>
              </Grid>
              <Grid columns="2" gap="4">
                <Flex direction="column" gap="2">
                  <Label htmlFor="email">Email</Label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    className="input"
                  />
                </Flex>
                <Flex direction="column" gap="2">
                  <Label htmlFor="phone">Phone</Label>
                  <input
                    type="text"
                    name="phone"
                    id="phone"
                    className="input"
                  />
                </Flex>
              </Grid>
            </Flex>
          </CardContent>
          <CardFooter>
            <Button type="submit" disabled={formIsPending}>
              Create Contract
            </Button>
          </CardFooter>
        </Card>
      </form>
    </Flex>
  );
}
